import {
  Box,
  Button,
  Divider,
  HStack,
  VStack,
  Badge,
  Text,
  Spinner,
  Grid,
} from "@chakra-ui/react";
import DraftCampaigns from "./DraftCampaigns";
import { FiPlus } from "react-icons/fi";
import { Link } from "react-router-dom";
import StatCard from "../Components/StatCard";
import { useCallback, useEffect, useState } from "react";
import {
  KYDInternalCampaign,
  KYDCampaignMetricsSummary,
} from "@common/interfaces/KYDCampaign";
import actions from "src/actions";
import CampaignsSentTable from "./CampaignsSentTable";
import { formatDate } from "src/utils";
import dayjs from "dayjs";
import { useOrganization } from "../../EventDetail/hooks/useOrganization";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const CampaignsOverview = ({
  permissions = [],
}: {
  permissions?: KYDScope[];
}) => {
  const [campaigns, setCampaigns] = useState<KYDInternalCampaign[]>([]);
  const [summary, setSummary] = useState<KYDCampaignMetricsSummary>();
  const [loading, setLoading] = useState({
    dashboard: true,
  });
  const { organization } = useOrganization();

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading({
        dashboard: true,
      });
      const response = await actions.ADMIN.fetchCampaignsDashboard();
      setCampaigns(response.campaigns);
      setSummary(response.metrics);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading({
        dashboard: false,
      });
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  if (loading.dashboard) {
    return <Spinner />;
  }
  return (
    <VStack w={"100%"} alignItems={"flex-start"}>
      <Box w="100%">
        <HStack justifyContent={"space-between"} w="100%">
          <Text fontSize="32px" fontWeight="400" mb={"32px"}>
            Campaigns Overview
          </Text>
          <ShowIfHasScope
            permissions={permissions}
            checkFor={["crm/campaigns:write", "crm/write"]}
          >
            <Link to={"/campaigns/templates"}>
              <Button
                bgColor={"#000000"}
                color={"#FFFFFF"}
                borderRadius={"1000px"}
                fontWeight={"600"}
                fontSize={"16px"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"10px"}
                padding={"14px 16px 14px 24px"}
              >
                <Text>New Campaign</Text>
                <FiPlus size={24} />
              </Button>
            </Link>
          </ShowIfHasScope>
        </HStack>

        <VStack mb={4} w="full" alignItems={"flex-start"} gap={0}>
          <Text fontSize="24px" fontWeight="600">
            Last 30 Days
          </Text>
          {summary.created_at && (
            <Text fontSize="xs" color="gray.500">
              Last updated on{" "}
              {dayjs(summary.created_at)
                .tz(organization?.settings.timezone || "America/New_York")
                .format("dddd, MMMM Do")}{" "}
              at{" "}
              {dayjs(summary.created_at)
                .tz(organization?.settings.timezone || "America/New_York")
                .format("h:mmA")}
            </Text>
          )}
        </VStack>

        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={"24px"}
          alignItems="stretch"
        >
          <StatCard title="Opens" value={summary?.display?.opens} />
          <StatCard
            title="Revenue"
            value={summary?.display?.revenue}
            isKYD
            highlight
          />
          <StatCard
            title="Tickets"
            value={summary?.display?.tickets}
            isKYD
            highlight
          />
          <StatCard
            title="Campaigns Sent"
            value={summary?.display?.campaigns_sent}
          />
          <StatCard title="Contacts" value={summary?.display?.contacts} />
          <StatCard title="Open Rate" value={summary?.display?.open_rate} />
          <StatCard
            title="Click Through Rate"
            value={summary?.display?.click_through_rate}
          />
          <StatCard
            title="Unsubscribes"
            value={summary?.display?.unsubscribes}
          />
        </Grid>

        <Badge
          boxShadow={"0px 2px 4px 0px #00000009"}
          mt={"12px"}
          bgColor={"#D3F227"}
          color={"#000000"}
          borderRadius={"1000px"}
          textColor={"#111111BF"}
          padding={"10px"}
          fontSize={"12px"}
          fontWeight={"500"}
          textAlign={"center"}
          width={"fit-content"}
          height={"31px"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          gap={"4px"}
          textTransform={"none"}
        >
          <img
            src="/crown.png"
            alt="KYD"
            style={{ opacity: "0.70" }}
            width={16}
            height={16}
          />
          <Text mr={0.5} ml={0.5}>
            =
          </Text>
          <Text as="span" display="inline" whiteSpace="nowrap">
            KYD Sold Tickets
          </Text>
        </Badge>
      </Box>
      <Box
        display={
          campaigns.filter((c) => c.status === "DRAFT").length > 0
            ? "flex"
            : "none"
        }
        position="relative"
        width={"100%"}
      >
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/campaigns:write", "crm/write"]}
        >
          <>
            <DraftCampaigns
              campaigns={campaigns.filter((c) => c.status === "DRAFT")}
              permissions={permissions}
              invalidateCampaigns={async () => {
                await fetchCampaigns();
              }}
            />
            <Box
              position="absolute"
              right="0"
              top="0"
              bottom="0"
              width="100px"
              background="linear-gradient(90deg, rgba(255,255,255,0) 0%, #FFFFFF 100%)"
              pointerEvents="none"
            />
          </>
        </ShowIfHasScope>
      </Box>
      <Divider borderColor={"#0000001A"} mt={"24px"} mb={"24px"} />
      <Text fontSize="24px" fontWeight="600" mb={"12px"}>
        Campaigns
      </Text>
      <Box
        w={"100%"}
        overflowX={"auto"}
        maxWidth={"100%"}
        overflowY={"auto"}
        maxHeight={"750px"}
      >
        {campaigns.length > 0 ? (
          <CampaignsSentTable
            permissions={permissions}
            campaigns={campaigns.filter((c) => c.status !== "DRAFT")}
            invalidateCampaigns={async () => {
              await fetchCampaigns();
            }}
          />
        ) : (
          <Text color="gray.500">No campaigns created yet</Text>
        )}
      </Box>
    </VStack>
  );
};

export default CampaignsOverview;
